export interface FieldError {
  field: string
  code: string
  detail?: string
  meta?: Record<string, unknown>
}

export interface AppErrorShape {
  kind: "problem" | "network" | "timeout" | "unknown"
  /** 0 for transport failures. */
  status: number
  /** snake_case, from the `type` suffix (DW-02); "" when there is none. */
  code: string
  /** The raw RFC 9457 `type` URI; "" when the failure was not a response. */
  type: string
  title: string
  /** Already localised by the server via Accept-Language. */
  detail?: string
  instance?: string
  /** Never undefined; empty when there are none, so call sites need no guard. */
  fields: FieldError[]
  /** Matches X-Request-Id and the server logs. Always surfaced to the user. */
  requestId?: string
  /**
   * Seconds, from the `Retry-After` header on a 429 or 503 (FR-DSH-IAM-13). A
   * response header, not a body member, per RFC 9110.
   */
  retryAfter?: number
  /**
   * Every top-level problem member that is not a standard one (type, title,
   * status, detail, instance, requestId, errors): the backend spreads its
   * per-code `Meta` here (LLD §5.4). Never undefined; `{}` when none.
   */
  extensions: Record<string, unknown>
}

interface ProblemBody {
  type?: string
  title?: string
  status?: number
  detail?: string
  instance?: string
  requestId?: string
  errors?: FieldError[]
  [extension: string]: unknown
}

/**
 * The one error type in the application. Everything that can fail produces this
 * or throws it (LLD §1.4). Branch on `code` only.
 *
 * A real Error subclass rather than a plain object: throwing non-Errors loses
 * the stack, and a network failure with no stack is a support ticket nobody can
 * answer.
 */
export class AppError extends Error implements AppErrorShape {
  readonly kind: AppErrorShape["kind"]
  readonly status: number
  readonly code: string
  readonly type: string
  readonly title: string
  readonly detail?: string
  readonly instance?: string
  readonly fields: FieldError[]
  readonly requestId?: string
  readonly retryAfter?: number
  readonly extensions: Record<string, unknown>

  constructor(shape: AppErrorShape, options?: { cause?: unknown }) {
    super(shape.title || shape.code || shape.kind, options)
    this.name = "AppError"
    this.kind = shape.kind
    this.status = shape.status
    this.code = shape.code
    this.type = shape.type
    this.title = shape.title
    this.fields = shape.fields
    this.extensions = shape.extensions
    if (shape.detail !== undefined) this.detail = shape.detail
    if (shape.instance !== undefined) this.instance = shape.instance
    if (shape.requestId !== undefined) this.requestId = shape.requestId
    if (shape.retryAfter !== undefined) this.retryAfter = shape.retryAfter
  }

  /** A failure that never produced a response: offline, DNS, CORS, abort. */
  static transport(kind: "network" | "timeout" | "unknown", cause?: unknown): AppError {
    return new AppError(
      { kind, status: 0, code: "", type: "", title: "", fields: [], extensions: {} },
      { cause },
    )
  }

  /**
   * A non-2xx response. A body that is not problem+json (a proxy's HTML 502)
   * still yields an AppError with the real status: a parse failure must never
   * downgrade a 502 into "unknown".
   */
  static async fromResponse(res: Response): Promise<AppError> {
    const headerRequestId = res.headers.get("X-Request-Id") ?? undefined
    const retryAfter = parseRetryAfter(res.headers.get("Retry-After"))

    let body: ProblemBody = {}
    if (isProblemResponse(res)) {
      try {
        const parsed: unknown = await res.json()
        if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
          body = parsed as ProblemBody
        }
      } catch {
        /* keep the status */
      }
    }

    const { type, title, status, detail, instance, requestId, errors, ...extensions } = body
    const rid = requestId ?? headerRequestId
    return new AppError({
      kind: "problem",
      status: status ?? res.status,
      code: codeFromType(type),
      type: type ?? "",
      title: title ?? res.statusText,
      fields: Array.isArray(errors) ? errors : [],
      extensions,
      ...(detail !== undefined && { detail }),
      ...(instance !== undefined && { instance }),
      ...(rid !== undefined && { requestId: rid }),
      ...(retryAfter !== undefined && { retryAfter }),
    })
  }
}

/**
 * The machine code is the last kebab-case segment of `type` (DW-02), in the
 * snake_case the spec's `x-problem-codes` keys use:
 * `…/problems/msisdn-invalid` → `msisdn_invalid`.
 */
export function codeFromType(type: string | undefined): string {
  if (!type) return ""
  const last = type.split(/[/#]/).filter(Boolean).at(-1) ?? ""
  return last.replaceAll("-", "_")
}

export const isProblemResponse = (res: Response): boolean =>
  res.headers.get("Content-Type")?.includes("application/problem+json") ?? false

/**
 * RFC 9110 §10.2.3: either delta-seconds ("120") or an HTTP-date. Returns
 * undefined for a missing or unparseable header rather than throwing --
 * this is a UX nicety (the countdown), never something correctness depends on.
 */
export function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined
  const trimmed = header.trim()
  // A purely numeric string is delta-seconds or nothing -- never fall through
  // to Date.parse for it. V8 parses a bare "-5" as a valid (nonsensical) date,
  // which would otherwise silently misread a malformed header as a real time.
  if (/^-?\d+$/.test(trimmed)) {
    const seconds = Number(trimmed)
    return seconds >= 0 ? seconds : undefined
  }
  const when = Date.parse(trimmed)
  if (Number.isNaN(when)) return undefined
  return Math.max(0, Math.round((when - Date.now()) / 1000))
}

export const fieldError = (e: AppError, field: string): FieldError | undefined =>
  e.fields.find((f) => f.field === field)

export const isAppError = (e: unknown): e is AppError => e instanceof AppError
